'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

interface FormValues {
  email: string;
  password: string;
  remember?: boolean;
  username?: string;
  confirmPassword?: string;
}

interface EmailAuthFormProps {
  mode: 'login' | 'signup';
}

export default function EmailAuthForm({ mode }: EmailAuthFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const { signIn, signUp } = useAuth();
  const router = useRouter();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>();

  const passwordValue = watch('password', '');

  const onSubmit = async (formValues: FormValues) => {
    setLoading(true);
    try {
      if (mode === 'login') {
        const result = await signIn(formValues.email, formValues.password);
        if (result?.session) {
          toast.success('Welcome back! Redirecting to dashboard...');
          window.location.href = '/dashboard';
        } else {
          toast.info('Sign-in complete. Redirecting...');
          window.location.href = '/dashboard';
        }
      } else {
        const result = await signUp(formValues.email, formValues.password, {
          fullName: formValues.username || '',
          username: formValues.username || '',
        });
        
        // Supabase returns session: null if email verification is required
        if (result?.session) {
          toast.success('Account created! Redirecting to dashboard...');
          window.location.href = '/dashboard';
        } else {
          toast.success('Account created! Please check your email for a verification link.');
        }
      }
    } catch (err: any) {
      toast.error(err?.message || 'Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {mode === 'signup' && (
        <div>
          <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: 'hsl(var(--muted))' }}>
            Username
          </label>
          <input
            {...register('username', { required: 'Username is required', minLength: { value: 3, message: 'At least 3 characters' } })}
            type="text"
            placeholder="cryptotasker"
            className="w-full px-4 py-3 rounded-xl text-sm outline-none border transition-all"
            style={{
              background: '#ffffff',
              borderColor: errors.username ? 'hsl(var(--danger))' : 'hsl(var(--border))',
              color: 'hsl(var(--text))',
            }}
          />
          {errors.username && <p className="mt-1 text-xs" style={{ color: 'hsl(var(--danger))' }}>{errors.username.message}</p>}
        </div>
      )}

      <div>
        <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: 'hsl(var(--muted))' }}>
          Email Address
        </label>
        <input
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
          })}
          type="email"
          placeholder="you@example.com"
          className="w-full px-4 py-3 rounded-xl text-sm outline-none border transition-all"
          style={{
            background: '#ffffff',
            borderColor: errors.email ? 'hsl(var(--danger))' : 'hsl(var(--border))',
            color: 'hsl(var(--text))',
          }}
        />
        {errors.email && <p className="mt-1 text-xs" style={{ color: 'hsl(var(--danger))' }}>{errors.email.message}</p>}
      </div>

      <div>
        <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: 'hsl(var(--muted))' }}>
          Password
        </label>
        <div className="relative">
          <input
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 8, message: 'At least 8 characters required' },
            })}
            type={showPassword ? 'text' : 'password'}
            placeholder="••••••••"
            className="w-full px-4 py-3 pr-11 rounded-xl text-sm outline-none border transition-all"
            style={{
              background: '#ffffff',
              borderColor: errors.password ? 'hsl(var(--danger))' : 'hsl(var(--border))',
              color: 'hsl(var(--text))',
            }}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2"
            style={{ color: 'hsl(var(--muted))' }}
          >
            <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
          </button>
        </div>
        {errors.password && <p className="mt-1 text-xs" style={{ color: 'hsl(var(--danger))' }}>{errors.password.message}</p>}
      </div>

      {mode === 'signup' && (
        <div>
          <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: 'hsl(var(--muted))' }}>
            Confirm Password
          </label>
          <div className="relative">
            <input
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (v) => v === passwordValue || 'Passwords do not match',
              })}
              type={showConfirm ? 'text' : 'password'}
              placeholder="••••••••"
              className="w-full px-4 py-3 pr-11 rounded-xl text-sm outline-none border transition-all"
              style={{
                background: '#ffffff',
                borderColor: errors.confirmPassword ? 'hsl(var(--danger))' : 'hsl(var(--border))',
                color: 'hsl(var(--text))',
              }}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-1/2 -translate-y-1/2"
              style={{ color: 'hsl(var(--muted))' }}
            >
              <Icon name={showConfirm ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
            </button>
          </div>
          {errors.confirmPassword && <p className="mt-1 text-xs" style={{ color: 'hsl(var(--danger))' }}>{errors.confirmPassword.message}</p>}
        </div>
      )}

      {mode === 'login' && (
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: 'hsl(var(--text-secondary))' }}>
            <input {...register('remember')} type="checkbox" className="w-4 h-4 rounded" />
            Remember me
          </label>
          <a href="#" className="text-xs font-semibold transition-all duration-150 hover:underline hover:scale-[1.03]" style={{ color: 'hsl(var(--primary))' }}>
            Forgot password?
          </a>
        </div>
      )}

      {mode === 'signup' && (
        <p className="text-xs" style={{ color: 'hsl(var(--muted))' }}>
          By signing up you agree to our{' '}
          <a href="#" style={{ color: 'hsl(var(--primary))' }}>Terms of Service</a> and{' '}
          <a href="#" style={{ color: 'hsl(var(--primary))' }}>Privacy Policy</a>.
        </p>
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full py-3 rounded-xl font-bold text-sm transition-all duration-150 hover:scale-[1.02] active:scale-[0.97] hover:shadow-lg disabled:opacity-60 flex items-center justify-center gap-2"
        style={{
          background: 'hsl(var(--primary))',
          color: '#fff',
          minHeight: '48px',
          boxShadow: '0 4px 20px hsl(217 91% 60% / 0.3)',
        }}
      >
        {loading ? (
          <>
            <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
            {mode === 'login' ? 'Signing in...' : 'Creating account...'}
          </>
        ) : mode === 'login' ? (
          'Sign In'
        ) : (
          'Create Account'
        )}
      </button>

    </form>
  );
}